// src/hooks/useValidateNpi.ts
import { useState, useEffect } from "react";
import axios from "axios";
import { SERVER_URL } from "../config";

interface Result {
  valid: boolean;
  expiryDate?: string;
  loading: boolean;
  error?: string;
}

export function useValidateNpi(npi: string | null): Result {
  const [valid, setValid] = useState(false);
  const [expiryDate, setExpiryDate] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!npi) return;

    // ── try cache first ────────────────────────────────────────────
    const key = `npi:${npi}`;
    const cached = localStorage.getItem(key);
    if (cached) {
      const { valid: v, expiryDate: exp } = JSON.parse(cached);
      setValid(v);
      if (exp) setExpiryDate(exp);
      return; // already have it → skip fetch
    }

    let cancel = false;
    const fetchValidate = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const res = await axios.get(
          `${SERVER_URL}/api/provider/validate-npi/${npi}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancel) return;
        setValid(res.data.valid);
        const endDate = res.data.data?.enumeration_date_end;
        if (endDate) setExpiryDate(endDate);

        // store in cache (no TTL needed for dev, add one if desired)
        localStorage.setItem(
          key,
          JSON.stringify({ valid: res.data.valid, expiryDate: endDate })
        );
      } catch (err: any) {
        if (cancel) return;
        if (err.response?.status === 404) setValid(false);
        else setError(err.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    };

    fetchValidate();
    return () => {
      cancel = true;
    };
  }, [npi, token]);

  return { valid, expiryDate, loading, error };
}
